import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { statusIcons } from '../utils/iconMap';

const LoaderIcon = statusIcons.loading;
const EyeOffIcon = statusIcons.eyeOff;
const WifiOffIcon = statusIcons.wifiOff;
const InboxIcon = statusIcons.inbox;
import StateSurface from './ds/StateSurface';
import { RARITY_LABELS_TR, RARITY_UI, revealRarityKey } from '../utils/collectionUi';
import { pickActivityThumbnail, feedAccentClasses, formatRelativeTimeTr } from '../utils/activityFeedUi';
import { formatActivityFeedMessage, defaultActivityFeedUiConfig } from '../utils/activityFeedMessages';
import FeedEventThumb from './FeedEventThumb';
import { getApiBase } from '../utils/apiBase';
import { getActivityFeedHighlightsCached, getActivityFeedUiConfigCached } from '../utils/activityFeedCachedClient';
import { FeedEventIcon } from '../utils/iconMap';

const API_URL = getApiBase() + '/api';

/**
 * Canlı koleksiyon aktivitesi — herkese açık özet olaylar (kimlik güvenli).
 * @param {Array|null|undefined} sharedHighlights — Dashboard ile ortak yükleme: null=yükleniyor, dizi=olaylar; undefined=kendi isteği
 */
export default function ActivityFeed({ className = '', sharedHighlights }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(sharedHighlights === undefined);
  const [error, setError] = useState(null);
  const [uiConfig, setUiConfig] = useState(() => defaultActivityFeedUiConfig());

  useEffect(() => {
    let cancelled = false;
    const loadCfg = async () => {
      try {
        const cfgData = await getActivityFeedUiConfigCached();
        if (!cancelled && cfgData && typeof cfgData === 'object') {
          setUiConfig({ ...defaultActivityFeedUiConfig(), ...cfgData });
        }
      } catch {
        if (!cancelled) setUiConfig(defaultActivityFeedUiConfig());
      }
    };
    loadCfg();
    const id = window.setInterval(loadCfg, 32_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (sharedHighlights !== undefined) {
      if (sharedHighlights === null) {
        setEvents([]);
        setLoading(true);
        setError(null);
      } else {
        setEvents(Array.isArray(sharedHighlights) ? sharedHighlights : []);
        setLoading(false);
        setError(null);
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resData = await getActivityFeedHighlightsCached(12);
        if (!cancelled) {
          setEvents(Array.isArray(resData?.events) ? resData.events : []);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError('Bağlantı kurulamadı veya sunucu yanıt vermedi.');
          setEvents([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sharedHighlights, uiConfig.config_version]);

  if (loading) {
    return (
      <StateSurface
        variant="loading"
        title="Topluluk akışı yükleniyor…"
        className={`glass-effect rounded-2xl border border-white/10 p-6 ${className}`}
      />
    );
  }

  const feedDisabled = uiConfig.public_feed_enabled === false;

  if (feedDisabled) {
    return (
      <StateSurface
        variant="empty"
        icon={EyeOffIcon}
        title="Topluluk akışı şu an kapalı"
        description="Yönetici bu görünümü geçici olarak kapattı. Koleksiyonun ve görevlerin normal şekilde çalışmaya devam eder."
        className={`glass-effect rounded-2xl border border-white/10 p-6 text-center ${className}`}
        role="status"
      />
    );
  }

  if (error) {
    return (
      <StateSurface
        variant="error"
        icon={WifiOffIcon}
        title="Canlı akışa şimdi ulaşılamıyor"
        description={error}
        className={`glass-effect rounded-2xl border border-amber-500/20 p-6 text-center ${className}`}
        role="alert"
      />
    );
  }

  if (events.length === 0) {
    return (
      <StateSurface
        variant="empty"
        icon={InboxIcon}
        title="Henüz paylaşılabilir bir hareket yok"
        description="Aktivasyon, takılma veya nadir düşüşler burada belirecek — belki ilk görünen sen olursun."
        className={`glass-effect rounded-2xl border border-violet-500/15 p-6 text-center ${className}`}
      />
    );
  }

  const showThumbnails = uiConfig.show_thumbnails !== false;
  const showRarityBadges = uiConfig.show_rarity_badges !== false;
  const showRelativeTime = uiConfig.show_relative_time !== false;
  const templates = uiConfig.message_templates || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`glass-effect rounded-2xl border border-violet-500/20 p-5 ${className}`}
    >
      <div className="flex items-center justify-between gap-2 mb-4">
        <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Cinzel, serif' }}>
          Topluluktan
        </h2>
        <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Canlı</span>
      </div>
      <ul className="space-y-3 max-h-[420px] overflow-y-auto pr-1 -mr-1" aria-label="Aktivite akışı">
        {events.map((ev, i) => {
          const rk = revealRarityKey(ev.asset_rarity);
          const ui = RARITY_UI[rk] || RARITY_UI.common;
          const showRarity = ev.asset_rarity && ev.event_type !== 'collection_score_milestone';
          const thumbUrl = showThumbnails ? pickActivityThumbnail(ev) : null;
          const accent = feedAccentClasses(ev);
          const thumbSize = ev.event_type === 'rare_drop' ? 'md' : 'sm';
          return (
            <li
              key={`${ev.created_at}-${i}`}
              className={`rounded-xl p-3 flex gap-3 items-stretch transition-shadow ${accent.frame} ${accent.glow}`}
            >
              {showThumbnails ? (
                <FeedEventThumb
                  url={thumbUrl}
                  size={thumbSize}
                  className="border-white/15"
                >
                  <div className={`p-2 rounded-lg bg-black/50 border border-white/10 ${ui.chip}`}>
                    <FeedEventIcon eventType={ev.event_type} size="small" />
                  </div>
                </FeedEventThumb>
              ) : (
                <div className={`shrink-0 flex items-center justify-center p-2 rounded-xl border border-white/10 bg-black/40 ${ui.chip}`}>
                  <FeedEventIcon eventType={ev.event_type} size="small" />
                </div>
              )}
              <div className="min-w-0 flex-1 flex flex-col gap-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-gray-100 leading-snug flex-1 min-w-0">
                    {formatActivityFeedMessage(ev, templates)}
                  </p>
                  {showRelativeTime && (
                    <time
                      className="text-[10px] text-gray-500 tabular-nums shrink-0 hidden sm:block"
                      dateTime={ev.created_at}
                    >
                      {formatRelativeTimeTr(ev.created_at)}
                    </time>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-0.5">
                  {showRarityBadges && showRarity && (
                    <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${ui.badge}`}>
                      {RARITY_LABELS_TR[rk]}
                    </span>
                  )}
                  {showRelativeTime && (
                    <time className="text-[10px] text-gray-500 tabular-nums sm:hidden" dateTime={ev.created_at}>
                      {formatRelativeTimeTr(ev.created_at)}
                    </time>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
}
